import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SaveButton } from '../../../components/mui/Buttons/Buttons';
import {
  awardSkillxCommonBadge,
  getSkillxCommonBadges,
} from '../../../redux/features/skillxSlice';

/**
 * Award a common badge by hand.
 *
 * ONLY TIER 2 IS AWARDABLE. SkillX badges come in two tiers and this dialog can
 * only reach one of them. Module badges — one per project, fifteen of them —
 * are earned automatically the moment the project's submission is approved in
 * the assignments area; the student portal derives them from that approval and
 * never stores an award. There is deliberately no way to grant one here,
 * because a module badge with no approved work behind it would be a lie about
 * what the learner has done.
 *
 * What this awards is the cross-cutting kind: Perfect Attendance, Community
 * Helper, Presentation Star. Nothing in the system can observe those, which is
 * exactly why a person has to.
 *
 * Two things it does deliberately:
 *
 *   - THE SAME BADGE CAN BE GIVEN AGAIN. The spec allows a common badge to be
 *     earned more than once — three separate months of perfect attendance — so
 *     holding one already is not a reason to block. The dialog shows what the
 *     learner has instead, and lets the mentor decide.
 *   - THE REASON IS REQUIRED. The learner is shown this text next to the badge
 *     on their dashboard, so it is the difference between a medal and an
 *     explained one. The server rejects an award without it too.
 */
export default function AwardBadgeDialog({ batch, learner, onAwarded }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const { commonBadges, loading } = useSelector((state) => state.skillx);
  const catalogue = commonBadges?.catalogue || [];

  useEffect(() => {
    if (open && !catalogue.length) dispatch(getSkillxCommonBadges());
  }, [open, catalogue.length, dispatch]);

  // What they already hold, so a mentor about to give a fourth "Community
  // Helper" in a fortnight can see the other three first.
  const held = useMemo(
    () => new Map((learner?.awards || []).map((a) => [a.badgeId, a])),
    [learner]
  );

  const formik = useFormik({
    initialValues: { badge: null, note: '' },
    validationSchema: Yup.object().shape({
      badge: Yup.object().nullable().required('Choose a badge'),
      note: Yup.string().trim().required('Say what this is being awarded for'),
    }),
    onSubmit: (values, { resetForm }) => {
      dispatch(
        awardSkillxCommonBadge({
          admnNo: learner.admnNo,
          batchId: batch?.course_batch_id,
          badgeId: values.badge.badge_id,
          note: values.note.trim(),
        })
      ).then((res) => {
        const payload = res.payload;

        if (payload?.status === 'success') {
          enqueueSnackbar(payload.message || 'Badge awarded', { variant: 'success' });
          resetForm();
          setOpen(false);
          onAwarded?.();
          return;
        }

        // apiRequest resolves rather than rejecting, so a dropped connection
        // arrives here with no status at all.
        enqueueSnackbar(payload?.message || 'Could not award the badge', {
          variant: 'error',
        });
      });
    },
  });

  const chosen = formik.values.badge;
  const alreadyHas = chosen ? held.get(chosen.badge_id) : null;

  return (
    <GlobalDialog
      title={`Award a badge — ${learner?.name || ''}`}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={
        <AddButton size="small" action={() => setOpen(true)}>
          Award
        </AddButton>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              {catalogue.length ? (
                <Alert severity="info" icon={<Icon icon="solar:info-circle-linear" />}>
                  Only recognition badges are given by hand. Module badges are earned
                  automatically when you approve that project&apos;s submission.
                </Alert>
              ) : (
                // The catalogue being empty is not a loading glitch — it means
                // every badge defined so far is tied to a project. Saying only
                // "no options" would send a mentor looking for a bug.
                <Alert severity="warning">
                  There are no hand-awardable badges yet. Add one on the Achievement
                  Badges page and leave its assignment blank — a badge with an
                  assignment is earned by having that project approved, not given.
                </Alert>
              )}

              <Autocomplete
                options={catalogue}
                loading={loading}
                disabled={!catalogue.length}
                getOptionLabel={(option) => option.badge_name || ''}
                isOptionEqualToValue={(option, value) =>
                  option.badge_id === value?.badge_id
                }
                value={formik.values.badge}
                onChange={(e, option) => formik.setFieldValue('badge', option)}
                renderOption={(props, option) => {
                  const has = held.get(option.badge_id);
                  return (
                    <Box component="li" {...props} key={option.badge_id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {option.badge_name}
                          </Typography>
                          {has && <Chip size="small" label="Already held" />}
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {option.badge_description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Badge"
                    error={formik.touched.badge && Boolean(formik.errors.badge)}
                    helperText={formik.touched.badge && formik.errors.badge}
                  />
                )}
              />

              {alreadyHas && (
                <Alert severity="warning">
                  {learner?.name} already holds {chosen.badge_name}. These can be earned
                  more than once, so this will add another rather than replace it.
                </Alert>
              )}

              <TextField
                fullWidth
                multiline
                minRows={3}
                name="note"
                label="What is this for?"
                placeholder="Top marks on the SWOT presentation, 12 March"
                value={formik.values.note}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.note && Boolean(formik.errors.note)}
                helperText={
                  (formik.touched.note && formik.errors.note) ||
                  'The learner sees this next to the badge on their dashboard.'
                }
              />

              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <CancelButton action={() => setOpen(false)}>Cancel</CancelButton>
                <SaveButton type="submit" disabled={loading || !catalogue.length}>
                  Award badge
                </SaveButton>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      }
    />
  );
}
